// The anti-player: the only other person who remembers previous loops.
//
// Phase 6 asks for "authored strategy stages rather than uncontrolled
// procedural AI", so the *sequence* of what he tries and the day he tries it
// are fixed in antiPlayer.json, and only the ground he chooses adapts — from
// the routes, allies, training sites and garrisons he has watched the player
// use across earlier loops. That keeps his escalation testable while still
// making him feel like he is learning, because he is: everything he knows
// lives in PermanentState and therefore survives the player's death, which is
// the entire point of him.

import type {
  AntiPlayerPersistentState,
  AntiPlayerStrategy,
  BattleId,
  FactionId,
  GameState,
  LocationId,
  LoopState,
  PermanentState,
  RouteId,
} from "../types"
import { antiPlayerStrategies, getRouteById } from "./content"

/** Set for the rest of the loop once his move for it has been dealt with, either way. */
const MOVE_RESOLVED_FLAG = "anti_player_move_resolved"

/** How many times he must be met before the temple records can be made to name him. */
export const ENCOUNTERS_BEFORE_IDENTIFIABLE = 2

export function getStrategyForLevel(level: number): AntiPlayerStrategy | null {
  return antiPlayerStrategies.find((strategy) => strategy.level === level) ?? null
}

/**
 * The stage he is currently on. currentStrategyLevel counts the methods the
 * player has already beaten out of him, so the stage in play is the next one
 * up — capped at the last authored stage, which he repeats rather than running
 * out of ideas.
 */
/** The stage a battle belongs to, so its result can be fed back into his memory. */
export function getStrategyForBattle(battleId: string): AntiPlayerStrategy | null {
  return antiPlayerStrategies.find((strategy) => strategy.battleId === battleId) ?? null
}

/** Whether a battle is one of his, including the optional final confrontation. */
export function isAntiPlayerBattle(battleId: string): boolean {
  return battleId === FINAL_BATTLE_ID || getStrategyForBattle(battleId) !== null
}

/** The stand-up fight at the temple, chosen instead of the severance. */
export const FINAL_BATTLE_ID = "anti_player_final_battle"

/** The stage at which he stops ambushing and simply waits to be answered. */
export const FINAL_STRATEGY_LEVEL = 8

export function isAwaitingReckoning(permanent: PermanentState): boolean {
  const strategy = getCurrentStrategy(permanent)
  return strategy !== null && strategy.level >= FINAL_STRATEGY_LEVEL
}

export function getCurrentStrategy(permanent: PermanentState): AntiPlayerStrategy | null {
  if (permanent.antiPlayerState.neutralized) return null
  const level = Math.min(
    permanent.antiPlayerState.currentStrategyLevel + 1,
    antiPlayerStrategies.length,
  )
  return getStrategyForLevel(level)
}

/** The location a route touches that isn't the one we came from. */
function routeEndpoints(routeId: RouteId): LocationId[] {
  const route = getRouteById(routeId)
  if (!route) return []
  return [route.fromLocationId, route.toLocationId]
}

/** Whichever value appears most often, or null for an empty list. Ties go to the earliest seen. */
function mostFrequent<T>(values: T[]): T | null {
  const counts = new Map<T, number>()
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1)
  let best: T | null = null
  let bestCount = 0
  for (const [value, count] of counts) {
    if (count > bestCount) {
      best = value
      bestCount = count
    }
  }
  return best
}

function largestGarrisonLocation(loop: LoopState): LocationId | null {
  let best: LocationId | null = null
  let bestCount = 0
  for (const [locationId, stacks] of Object.entries(loop.armiesByLocation)) {
    const count = stacks.reduce((total, stack) => total + stack.count, 0)
    if (count > bestCount) {
      best = locationId
      bestCount = count
    }
  }
  return best
}

function allyHomeLocation(anti: AntiPlayerPersistentState): LocationId | null {
  const factionId = mostFrequent(anti.knownPlayerAllies)
  if (!factionId) return null
  // Deliberately not the faction's homeLocationId: he strikes where the player
  // was seen doing the courting, and the courting happens at the seat of the
  // faction he has watched them return to.
  return ALLY_STRIKE_LOCATIONS[factionId] ?? null
}

/** Where courting a given faction actually happens, for the alliance-sabotage stage. */
const ALLY_STRIKE_LOCATIONS: Record<FactionId, LocationId> = {
  humans: "crownreach",
  high_elves: "starbow_academy",
  dwarves: "ironwatch_fortress",
  wood_elves: "goldenfields",
  spellcasters: "rivergate",
  good_spirits: "ruined_temple_first_dawn",
}

/**
 * The ground this stage will be fought on. Adaptive rules fall back to the
 * strategy's authored location whenever he has not yet watched the player
 * enough to have an opinion — a first loop should still see him act.
 */
export function resolveStrikeLocation(
  strategy: AntiPlayerStrategy,
  permanent: PermanentState,
  loop: LoopState,
): LocationId {
  const anti = permanent.antiPlayerState
  switch (strategy.targetRule) {
    case "player_location":
      return loop.currentLocationId
    case "known_route_node":
      return (
        mostFrequent(anti.knownPlayerRoutes.flatMap(routeEndpoints)) ??
        strategy.fallbackLocationId
      )
    case "training_site":
      return mostFrequent(anti.knownTrainingSites) ?? strategy.fallbackLocationId
    case "ally_home":
      return allyHomeLocation(anti) ?? strategy.fallbackLocationId
    case "largest_garrison":
      return largestGarrisonLocation(loop) ?? strategy.fallbackLocationId
    case "fixed":
    default:
      return strategy.fallbackLocationId
  }
}

export interface AntiPlayerMove {
  strategy: AntiPlayerStrategy
  locationId: LocationId
  day: number
}

/** What he intends to do this loop, or null once he has been neutralized. */
export function getPlannedMove(
  permanent: PermanentState,
  loop: LoopState,
): AntiPlayerMove | null {
  const strategy = getCurrentStrategy(permanent)
  if (!strategy) return null
  return {
    strategy,
    locationId: resolveStrikeLocation(strategy, permanent, loop),
    day: strategy.day,
  }
}

export function hasResolvedMoveThisLoop(loop: LoopState): boolean {
  return loop.worldFlags.includes(MOVE_RESOLVED_FLAG)
}

export interface AntiPlayerOutcome {
  strategy: AntiPlayerStrategy
  /** The ground he actually chose — where the consequences of an unmet move land. */
  locationId: LocationId
  /** Set when the player is there and it comes to blows. Null for the final stage. */
  battleId: BattleId | null
  /** Played before the battle, or on its own when there is no battle. */
  dialogueId: string | null
  flags: string[]
  /** Told to the player when the move landed somewhere they were not standing. */
  report: string | null
  knowledge: string[]
  /** Whether the player was there to meet him — drives encounterCount. */
  encountered: boolean
}

/**
 * Resolves his move for the days just elapsed, mirroring eventEffects: if the
 * player is standing on the ground he chose, they meet; otherwise the move
 * lands on the world and leaves a mark that lasts the rest of the loop.
 *
 * A stationary action keeps the player present for every day it spans, so a
 * week of training at the fortress he targeted is a week he cannot slip past;
 * a multi-day journey only counts as presence on the day of arrival.
 */
export function resolveAntiPlayerMove(
  prev: GameState,
  next: GameState,
): AntiPlayerOutcome | null {
  if (!next.loop.alive) return null
  if (hasResolvedMoveThisLoop(next.loop)) return null

  const move = getPlannedMove(next.permanent, next.loop)
  if (!move) return null

  // Only fires for a day that has just gone by. A move whose day was already
  // behind the player when the loop reached this code (they were dead, or the
  // stage advanced late) is simply missed rather than fired retroactively.
  const firstNewDay = prev.loop.currentDay + 1
  if (move.day < firstNewDay || move.day > next.loop.currentDay) return null

  const stayedPut = prev.loop.currentLocationId === next.loop.currentLocationId
  const playerPresent = stayedPut
    ? move.locationId === next.loop.currentLocationId
    : move.locationId === next.loop.currentLocationId && move.day === next.loop.currentDay

  const { strategy } = move

  if (playerPresent) {
    // The final stage has no battle attached: he is waiting, not attacking,
    // and how that ends is the player's decision at the location rather than
    // something that resolves itself the moment they arrive.
    return {
      strategy,
      locationId: move.locationId,
      battleId: strategy.battleId,
      dialogueId: strategy.encounterDialogueId,
      flags: [MOVE_RESOLVED_FLAG],
      report: null,
      knowledge: strategy.grantsKnowledge,
      encountered: true,
    }
  }

  return {
    strategy,
    locationId: move.locationId,
    battleId: null,
    dialogueId: null,
    flags: [MOVE_RESOLVED_FLAG, ...strategy.absentFlags],
    report: strategy.absentReport || null,
    knowledge: strategy.grantsKnowledge,
    encountered: false,
  }
}

/** Records that the player met him face to face. */
export function recordEncounter(permanent: PermanentState): PermanentState {
  return {
    ...permanent,
    antiPlayerState: {
      ...permanent.antiPlayerState,
      encounterCount: permanent.antiPlayerState.encounterCount + 1,
    },
  }
}

/**
 * Records that a method has been beaten, which is what moves him on to the
 * next authored stage. He never repeats something that has already failed —
 * that is the whole of his adaptation, and it is why the player sees his
 * tactics change across loops rather than within one.
 */
export function recordMethodFailed(
  permanent: PermanentState,
  methodId: string,
): PermanentState {
  const anti = permanent.antiPlayerState
  if (anti.failedMethods.includes(methodId)) return permanent
  return {
    ...permanent,
    antiPlayerState: {
      ...anti,
      failedMethods: [...anti.failedMethods, methodId],
      currentStrategyLevel: Math.min(
        anti.currentStrategyLevel + 1,
        antiPlayerStrategies.length - 1,
      ),
      defeatCount: anti.defeatCount + 1,
    },
  }
}

/** Watches a route the player took. He is not guessing; he saw. */
export function observeRoute(permanent: PermanentState, routeId: RouteId): PermanentState {
  const anti = permanent.antiPlayerState
  return {
    ...permanent,
    antiPlayerState: { ...anti, knownPlayerRoutes: [...anti.knownPlayerRoutes, routeId] },
  }
}

export function observeAlly(permanent: PermanentState, factionId: FactionId): PermanentState {
  const anti = permanent.antiPlayerState
  return {
    ...permanent,
    antiPlayerState: { ...anti, knownPlayerAllies: [...anti.knownPlayerAllies, factionId] },
  }
}

export function observeTrainingSite(
  permanent: PermanentState,
  locationId: LocationId,
): PermanentState {
  const anti = permanent.antiPlayerState
  return {
    ...permanent,
    antiPlayerState: {
      ...anti,
      knownTrainingSites: [...anti.knownTrainingSites, locationId],
    },
  }
}

/**
 * Whether the temple records can be made to give up his name. Needs both the
 * scholarship to read them and enough encounters to know there is a name to
 * look for — the records are useless to someone who has not yet met him.
 */
export function canDiscoverIdentity(permanent: PermanentState): boolean {
  return (
    !permanent.antiPlayerState.identityDiscovered &&
    permanent.antiPlayerState.encounterCount >= ENCOUNTERS_BEFORE_IDENTIFIABLE
  )
}

export function discoverIdentity(permanent: PermanentState): PermanentState {
  return {
    ...permanent,
    antiPlayerState: { ...permanent.antiPlayerState, identityDiscovered: true },
  }
}

export function discoverMotivation(permanent: PermanentState): PermanentState {
  return {
    ...permanent,
    antiPlayerState: { ...permanent.antiPlayerState, motivationDiscovered: true },
  }
}

/**
 * The morally clean resolution. Killing him has been possible since the first
 * hired knife and it has never once stuck — he wakes in Dawnmere the next
 * morning with everything he knows intact, which is exactly what the player
 * does. Ending him for good means giving him the one thing he has never had:
 * a reason to believe the loop can be closed by someone other than himself.
 *
 * That requires knowing who he is and what he wants (both earned, not told),
 * the history to prove the ritual is what is holding the loop open, and the
 * standing to be believed when saying so.
 */
export function canSeverAntiPlayer(permanent: PermanentState): boolean {
  const anti = permanent.antiPlayerState
  return (
    !anti.neutralized &&
    anti.identityDiscovered &&
    anti.motivationDiscovered &&
    permanent.abilities["history"] === "master" &&
    permanent.abilities["diplomacy"] === "master"
  )
}

/** Explains, for the locked entry, what the severance is still missing. */
export function describeSeveranceRequirements(permanent: PermanentState): string[] {
  const anti = permanent.antiPlayerState
  const missing: string[] = []
  if (!anti.identityDiscovered) missing.push("knowing who he actually is")
  if (!anti.motivationDiscovered) missing.push("knowing what he actually wants")
  if (permanent.abilities["history"] !== "master") {
    missing.push("History (master) — the records that prove what holds the loop open")
  }
  if (permanent.abilities["diplomacy"] !== "master") {
    missing.push("Diplomacy (master) — the standing to be believed")
  }
  return missing
}

export function severAntiPlayer(permanent: PermanentState): PermanentState {
  return {
    ...permanent,
    antiPlayerState: { ...permanent.antiPlayerState, neutralized: true },
  }
}

/** A short line on what he is currently doing, for the Spirit and Journal screens. */
export function describeCurrentStrategy(permanent: PermanentState): string {
  if (permanent.antiPlayerState.neutralized) {
    return "He is not coming. Whatever else is left to do, it will not include him."
  }
  const strategy = getCurrentStrategy(permanent)
  if (!strategy) return "You have no idea what he intends."
  if (permanent.antiPlayerState.encounterCount === 0) {
    return "You have not met him yet, and have no reason to think anybody is hunting you."
  }
  return `Last time you beat him, he stopped trying that. Stage ${strategy.level}: ${strategy.name}.`
}
